import { useCallback, useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { Image3D } from '~/domain/patient/image3d';
import { Patient } from '~/domain/patient/patient';
import { StructureSet } from '~/domain/patient/structureset';
import { StructureSetReadModel } from '~/domain/readmodels/StructureSetReadModel';
import { CopyStructureSetResult } from '~/domain/services/structureSetService/structureSetCopyService';
import { useCopyStructureSet } from '~/hooks/structure-set/useCopyStructureSet';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';
import { environmentState } from '~/state/atoms/engineAtoms';
import {
    patientRepositoryState,
    selectedPatientIdState,
} from '~/state/atoms/patientAtoms';
import { enqueueSnackbar } from '~/utils/notifications';
import { CopyTarget } from './types';

/**
 * Copy-to machinery for a single source structure set: resolves the patient,
 * domain structure set, and source image, and dispatches the chosen copy
 * variant. `onSuccess` runs on the success path only, before snackbars.
 */
export function useCopyStructureSetFlow(
    structureSet: StructureSetReadModel | null,
    onSuccess: () => void
): {
    patient: Patient | null;
    domainStructureSet: StructureSet | null;
    sourceImage: Image3D | null;
    handleConfirmCopy: (target: CopyTarget) => Promise<void>;
} {
    const env = useRecoilValue(environmentState);
    const patientRepository = useRecoilValue(patientRepositoryState);
    const selectedPatientId = useRecoilValue(selectedPatientIdState);
    const {
        copyStructureSet,
        copyIntoExisting,
        copyWithRegistration,
        copyWithRegistrationIntoExisting,
        copySameFrameOfReference,
        copySameFrameOfReferenceIntoExisting,
    } = useCopyStructureSet();
    const { setEnvironmentStructureSet } = useViewportEnvironment();

    const patient = useMemo(
        () =>
            selectedPatientId
                ? (patientRepository.getByPatientId(selectedPatientId) ?? null)
                : null,
        [patientRepository, selectedPatientId]
    );

    const domainStructureSet = useMemo(() => {
        if (!structureSet || !patient) {
            return null;
        }
        return (
            patient.structureSets.find(
                (ss) =>
                    ss.dicomStructureSet.sopInstanceUid ===
                    structureSet.dicomUid
            ) ?? null
        );
    }, [structureSet, patient]);

    const sourceImage = domainStructureSet?.image ?? null;

    const handleConfirmCopy = useCallback(
        async (target: CopyTarget) => {
            if (!domainStructureSet || !patient || !env) {
                return;
            }

            try {
                let result: CopyStructureSetResult;

                if (target.targetStructureSet) {
                    if (target.registration) {
                        result = await copyWithRegistrationIntoExisting(
                            domainStructureSet,
                            target.targetStructureSet,
                            target.image,
                            target.registration,
                            target.selectedRoiNumbers,
                            target.overwriteExisting
                        );
                    } else if (target.sameFrameOfReference) {
                        result = await copySameFrameOfReferenceIntoExisting(
                            domainStructureSet,
                            target.targetStructureSet,
                            target.image,
                            target.selectedRoiNumbers,
                            target.overwriteExisting
                        );
                    } else {
                        result = await copyIntoExisting(
                            domainStructureSet,
                            target.targetStructureSet,
                            target.selectedRoiNumbers,
                            target.overwriteExisting
                        );
                    }
                } else {
                    if (target.registration) {
                        result = await copyWithRegistration(
                            domainStructureSet,
                            target.image,
                            target.registration,
                            patient,
                            target.selectedRoiNumbers
                        );
                    } else if (target.sameFrameOfReference) {
                        result = await copySameFrameOfReference(
                            domainStructureSet,
                            target.image,
                            patient,
                            target.selectedRoiNumbers
                        );
                    } else {
                        result = await copyStructureSet(
                            domainStructureSet,
                            target.image,
                            patient,
                            target.selectedRoiNumbers
                        );
                    }
                }

                onSuccess();

                setEnvironmentStructureSet(
                    [result.structureSet],
                    patient.dataSet.PatientID
                );

                let emptyCount = 0;
                for (const roiNumber of result.copiedRoiNumbers) {
                    const roi =
                        result.structureSet.dicomStructureSet.getRoi(roiNumber);
                    if (!roi) {
                        continue;
                    }
                    // Skip point ROIs (MARKER, ISOCENTER, REGISTRATION) as they're expected to have no volume
                    if (roi.isPointRoi) {
                        continue;
                    }
                    const hasVolume = roi.contours.some(
                        (c) => c.data.length >= 3
                    );
                    if (!hasVolume) {
                        emptyCount++;
                    }
                }

                const skippedCount = result.skippedRois.length;

                if (skippedCount > 0 && emptyCount > 0) {
                    enqueueSnackbar(
                        `Structures copied successfully. ${skippedCount} structure(s) were skipped and ${emptyCount} structure(s) have empty volumes.`,
                        { variant: 'warning' }
                    );
                } else if (skippedCount > 0) {
                    enqueueSnackbar(
                        `Structures copied successfully. ${skippedCount} structure(s) were skipped.`,
                        { variant: 'warning' }
                    );
                } else if (emptyCount > 0) {
                    enqueueSnackbar(
                        `Structures copied successfully. ${emptyCount} structure(s) have empty volumes.`,
                        { variant: 'warning' }
                    );
                } else {
                    enqueueSnackbar('Structures copied successfully.', {
                        variant: 'success',
                    });
                }
            } catch (error) {
                console.error('Failed to copy structures:', error);
                enqueueSnackbar(
                    `Failed to copy structures: ${error instanceof Error ? error.message : error}`,
                    { variant: 'error' }
                );
            }
        },
        [
            domainStructureSet,
            patient,
            env,
            copyStructureSet,
            copyIntoExisting,
            copyWithRegistration,
            copyWithRegistrationIntoExisting,
            copySameFrameOfReference,
            copySameFrameOfReferenceIntoExisting,
            onSuccess,
            setEnvironmentStructureSet,
        ]
    );

    return { patient, domainStructureSet, sourceImage, handleConfirmCopy };
}
